import RAPIER from '@dimforge/rapier3d-compat';

export { RAPIER };

let initialized = false;

export async function ensureRapierReady(): Promise<typeof RAPIER> {
  if (!initialized) {
    await RAPIER.init();
    initialized = true;
  }
  return RAPIER;
}

export type ContactCallback = (
  collider1: number,
  collider2: number,
  started: boolean,
) => void;

export interface PhysicsWorld {
  rapier: typeof RAPIER;
  world: RAPIER.World;
  /**
   * Step the world. `onSubstep` runs before each fixed-timestep substep with the
   * substep dt — vehicle controllers and other ray-cast-driven systems should update
   * here so they stay in sync with the simulation, not the frame rate.
   */
  step(dt: number, onSubstep?: (substepDt: number) => void): void;
  /**
   * Subscribe to collision start/end events. Only colliders flagged with
   * `ActiveEvents.COLLISION_EVENTS` produce events. Returns an unsubscribe fn.
   */
  onContact(cb: ContactCallback): () => void;
}

export async function createPhysicsWorld(): Promise<PhysicsWorld> {
  const rapier = await ensureRapierReady();
  const world = new rapier.World({ x: 0, y: -9.81, z: 0 });
  // 120 Hz reduces wheel-ray tunneling on slopes — Rapier heightfields are one-sided,
  // so a single missed step can push the car below the surface permanently.
  world.timestep = 1 / 120;

  const eventQueue = new rapier.EventQueue(true);
  const listeners = new Set<ContactCallback>();

  let accumulator = 0;

  return {
    rapier,
    world,
    step(dt: number, onSubstep?: (substepDt: number) => void) {
      // Clamp pathological frame times so the sim doesn't spiral.
      accumulator += Math.min(dt, 0.1);
      while (accumulator >= world.timestep) {
        if (onSubstep) onSubstep(world.timestep);
        world.step(eventQueue);
        if (listeners.size > 0) {
          eventQueue.drainCollisionEvents((h1, h2, started) => {
            for (const cb of listeners) cb(h1, h2, started);
          });
        }
        accumulator -= world.timestep;
      }
    },
    onContact(cb: ContactCallback): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}
